import { AssistantCaller } from '@paragon/contract-library';

import { AppDispatch, RootState } from '../../store';

/**
 * A JSON schema for a tool's input. Kept loose: the model and WebMCP read it, and each tool checks what it needs.
 */
export type JsonSchema = Readonly<Record<string, unknown>>;

/**
 * What a tool returns. `summary` is read back to the model; `image` is a PNG data URL, for screenshots.
 */
export interface ToolResult {
  readonly summary: unknown;
  readonly image?: string;
}

export interface ToolContext {
  readonly dispatch: AppDispatch;
  readonly getState: () => RootState;
  readonly caller: AssistantCaller;
  /** The Assistant chat window the call came from. Agents have none. */
  readonly chatId?: string;
}

/**
 * A tool in WebMCP's shape (`name`, `description`, `inputSchema`, `execute`), plus what the Assistant needs to keep the
 * designer in charge.
 */
export interface AssistantTool<Input = Record<string, unknown>> {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: JsonSchema;

  /**
   * Whether the tool changes the project. The Assistant may only call these after the designer approves its Plan for
   * the Turn. Reading the project and changing the view don't count.
   */
  readonly changesProject: boolean;

  readonly execute: (input: Input, context: ToolContext) => Promise<ToolResult>;
}

/**
 * Thrown by a tool when the request can't be carried out, with a message for the model. Anything else thrown is
 * treated as an unexpected failure.
 */
export class ToolError extends Error {}

export type AnyAssistantTool = AssistantTool<any>;

export function defineTool<Input>(tool: AssistantTool<Input>): AnyAssistantTool {
  return tool;
}
